/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartOverviewConfig, Scale, ViewportDetail } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';

export const TIME_AXIS_OVERVIEW_PANE_SIZE = 0.25;
export const OVERVIEW_PANE_SIZE = 0.2;

export function getOverviewHeight(
  isTimeAxis: boolean,
  chartHeight: number,
  overview: ChartOverviewConfig | 'on' | 'off'
) {
  if (overview === 'off') {
    return 0;
  }

  if (overview === 'on') {
    return chartHeight * (isTimeAxis ? TIME_AXIS_OVERVIEW_PANE_SIZE : OVERVIEW_PANE_SIZE);
  }
  const isPercent = overview.height.includes('%');
  const ratio = parseFloat(overview.height);
  return isPercent ? (ratio / 100) * chartHeight : ratio;
}

export function updateViewportFromOverview(
  xAxisExtent: { min: number; max: number },
  isRtl: boolean,
  windowX: number,
  windowWidth: number,
  deltaX: number,
  deltaWidth: number,
  xScale: Scale,
  onViewportChange?: (detail: ViewportDetail) => void
) {
  const start = windowX + deltaX;
  const end = windowX + deltaX + windowWidth + deltaWidth;
  const xMin = Math.max(xAxisExtent.min, xScale.invert(isRtl ? end : start));
  const xMax = Math.min(xAxisExtent.max, xScale.invert(isRtl ? start : end));
  onViewportChange?.({
    xMin,
    xMax
  });
}

export function applyInitialZooming(params: {
  zoomAndScroll?: 'live' | 'off';
  initialZooming?: 'first' | 'last' | 'none';
  currentMin?: number;
  currentMax?: number;
  globalMin: number;
  globalMax: number;
  startOffset: number;
  endOffset: number;
  numGroups: number;
  availSpace?: Dimension;
  isHoriz?: boolean;
  getTextDimensions?: GetTextDimensionsType;
  tickLabelStyle?: Record<string, any>;
}) {
  const {
    zoomAndScroll,
    initialZooming,
    currentMin,
    currentMax,
    globalMin,
    globalMax,
    startOffset,
    endOffset,
    numGroups,
    availSpace,
    isHoriz,
    getTextDimensions,
    tickLabelStyle
  } = params;

  const axisWidth = isHoriz ? availSpace?.height : availSpace?.width;
  const fontDims = getTextDimensions?.('MW', tickLabelStyle as any);
  const fontH = fontDims?.height ?? 0;

  const maxNumGroups = Math.floor((axisWidth ?? 0) / (2 * (fontH || 1))) + startOffset;

  const maxViewportSize = numGroups > 0 ? (maxNumGroups / numGroups) * (globalMax - globalMin) : 0;

  let min = currentMin;
  let max = currentMax;

  if (zoomAndScroll === 'live' && (min == null || max == null)) {
    if (initialZooming === 'first') {
      min = min ?? globalMin + startOffset;
      max = max ?? Math.min((min as number) + maxViewportSize, globalMax + endOffset);
    } else if (initialZooming === 'last') {
      max = max ?? globalMax + endOffset;
      min = min ?? Math.max((max as number) - maxViewportSize, globalMin + startOffset);
    }
  }

  return { min, max };
}
